import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { getFlatReadingsUrl, getRealTimeUrl, getCameraNotSendingUrl, getCameraApiCode } from "../config/adminApi";

const REALTIME_POLL_INTERVAL_MS = 60000;

const AdminDataContext = createContext(null);

export const useAdminData = () => {
  const ctx = useContext(AdminDataContext);
  if (!ctx) {
    throw new Error("useAdminData must be used within AdminDataProvider");
  }
  return ctx;
};

export const AdminDataProvider = ({ children }) => {
  const { accounts } = useMsal();
  const location = useLocation();
  const [flatReadings, setFlatReadings] = useState([]);
  const [realTimeData, setRealTimeData] = useState(null);
  const [cameraNotSendingCount, setCameraNotSendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const endDate = new Date();
      const startDate = new Date(endDate);
      startDate.setDate(endDate.getDate() - 7);

      const flatReadingsUrl = getFlatReadingsUrl(startDate, endDate);
      const realTimeUrl = getRealTimeUrl();
      const cameraUrl = getCameraNotSendingUrl();
      const cameraCode = getCameraApiCode();

      const [flatReadingsResponse, realTimeResponse, cameraResponse] = await Promise.all([
        fetch(flatReadingsUrl, {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }),
        fetch(realTimeUrl, {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }),
        fetch(cameraUrl, {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "x-functions-key": cameraCode,
          },
        }).catch(() => ({ ok: false })),
      ]);

      let flatReadingsData = null;
      if (flatReadingsResponse.ok) {
        const text = await flatReadingsResponse.text();
        if (
          text.trim().toLowerCase().startsWith("<!doctype") ||
          text.trim().toLowerCase().startsWith("<html")
        ) {
          throw new Error("API returned HTML instead of JSON data");
        }
        flatReadingsData = JSON.parse(text);
      } else {
        throw new Error(
          `Flat readings API error: ${flatReadingsResponse.status} ${flatReadingsResponse.statusText}`
        );
      }

      let realTimeData = null;
      if (realTimeResponse.ok) {
        const text = await realTimeResponse.text();
        if (
          text.trim().toLowerCase().startsWith("<!doctype") ||
          text.trim().toLowerCase().startsWith("<html")
        ) {
          throw new Error("API returned HTML instead of JSON data");
        }
        realTimeData = JSON.parse(text);
      } else {
        throw new Error(`Real-time API error: ${realTimeResponse.status}`);
      }

      let processedData = {};
      if (flatReadingsData) {
        const dataArray = Array.isArray(flatReadingsData)
          ? flatReadingsData
          : [flatReadingsData];
        processedData = dataArray.reduce((acc, reading) => {
          if (reading && reading.Status === "Flat Reading") {
            if (!acc[reading.BuildingName]) {
              acc[reading.BuildingName] = {
                buildingName: reading.BuildingName,
                guid: reading.GUID,
                occurrences: [],
              };
            }
            acc[reading.BuildingName].occurrences.push({
              date: reading.Date,
              reading: reading.Reading,
            });
          }
          return acc;
        }, {});
      }

      if (realTimeData && !Array.isArray(realTimeData)) {
        realTimeData = [realTimeData];
      }

      let cameraCount = 0;
      if (cameraResponse && cameraResponse.ok) {
        try {
          const cameraJson = await cameraResponse.json();
          cameraCount = cameraJson.count ?? 0;
        } catch {
          // ignore
        }
      }
      setCameraNotSendingCount(cameraCount);

      setFlatReadings(Object.values(processedData));
      setRealTimeData(realTimeData);
      setError(null);
    } catch (err) {
      console.error("API Error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (accounts[0]?.idTokenClaims?.roles?.includes("Admin")) {
      setLoading(true);
      fetchData();
    }
  }, [accounts, fetchData]);

  useEffect(() => {
    if (location.pathname !== "/admin/realtime") return;
    const interval = setInterval(fetchData, REALTIME_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [location.pathname, fetchData]);

  const refresh = useCallback(() => {
    setError(null);
    setLoading(true);
    fetchData();
  }, [fetchData]);

  const value = {
    flatReadings,
    realTimeData,
    cameraNotSendingCount,
    loading,
    error,
    refresh,
  };

  return (
    <AdminDataContext.Provider value={value}>
      {children}
    </AdminDataContext.Provider>
  );
};
